import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { Supplier, SupplierCreate, SupplierFilter, SupplierUpdate } from '@core/types/supplier.type';

@Injectable({ providedIn: 'root' })
export class SupplierService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/suppliers`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<Supplier>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    const filter = (config.filter || {}) as SupplierFilter;
    if (filter.isActive !== undefined) params = params.set('filter.isActive', String(filter.isActive));
    if (filter.category) params = params.set('filter.category', filter.category);

    return this.http.get<ApiResponse<PaginationResult<Supplier>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading suppliers')),
    );
  }

  get(id: string): Observable<ApiResponse<Supplier>> {
    return this.http.get<ApiResponse<Supplier>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error loading supplier')),
    );
  }

  create(data: SupplierCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error creating supplier')),
    );
  }

  update(data: SupplierUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error updating supplier')),
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((e: HttpErrorResponse) => throwError(() => e.error?.message || 'Error deleting supplier')),
    );
  }
}

